import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, getToken } from '../../lib/api.js';
import { Loading, Stat, BarChart, Avatar, Stars, PageHeader } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { currency, STATUS_LABEL } from '../../lib/format.js';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/api/admin/stats').then(setStats).catch(() => setStats(null));
  }, []);

  if (!stats) return <Loading rows={3} />;

  const monthly = stats.monthly.map((m) => ({
    label: new Date(`${m.month}-01T12:00:00`).toLocaleDateString('en-IN', { month: 'short' }),
    value: m.revenue,
  }));

  const downloadReport = async () => {
    const res = await fetch('/api/admin/reports/bookings.csv', {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'healathome-bookings.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader title="Admin dashboard" subtitle="Platform health at a glance.">
        <button className="btn" onClick={downloadReport}>
          <Icon name="download" /> Export bookings CSV
        </button>
      </PageHeader>

      {(stats.pending_verifications > 0 || stats.open_complaints > 0) && (
        <div className="banner banner-warning">
          <Icon name="bell" />
          <span className="grow">
            {stats.pending_verifications > 0 && (
              <>
                <strong>{stats.pending_verifications}</strong> nurse
                {stats.pending_verifications > 1 ? 's' : ''} awaiting verification.{' '}
              </>
            )}
            {stats.open_complaints > 0 && (
              <>
                <strong>{stats.open_complaints}</strong> open complaint
                {stats.open_complaints > 1 ? 's' : ''}.
              </>
            )}
          </span>
          {stats.pending_verifications > 0 && (
            <Link to="/admin/verifications" className="btn btn-sm">
              Review now
            </Link>
          )}
        </div>
      )}

      <div className="grid grid-4 mb-3">
        <Stat label="Patients" value={stats.patients} sub="Registered accounts" icon="users" />
        <Stat
          label="Verified nurses"
          value={stats.verified_nurses}
          sub={`${stats.nurses} total registered`}
          icon="shield-check"
        />
        <Stat
          label="Bookings"
          value={stats.total_bookings}
          sub={`${stats.pending_bookings} awaiting response`}
          icon="clipboard"
        />
        <Stat
          label="Gross revenue"
          value={currency(stats.gross_revenue)}
          sub={`${currency(stats.upcoming_cash)} due at confirmed visits`}
          icon="wallet"
        />
      </div>

      <div className="detail-layout">
        <div className="stack">
          <section className="card">
            <div className="card-header">
              <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
                <Icon name="chart" size={16} />
              </span>
              <h2>Revenue by month</h2>
            </div>
            <div className="card-body">
              <BarChart data={monthly} format={(v) => `₹${Math.round(v / 100) / 10}k`} />
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h2>Bookings by service</h2>
            </div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th className="td-right">Bookings</th>
                    <th className="td-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byService.map((s) => (
                    <tr key={s.name}>
                      <td>{s.name}</td>
                      <td className="td-right">{s.n}</td>
                      <td className="bold td-right">{currency(s.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <aside className="stack sticky-aside">
          <section className="card">
            <div className="card-header">
              <h3>Booking status</h3>
            </div>
            <div className="card-body stack-sm">
              {stats.byStatus.map((s) => (
                <div key={s.status} className="row-between">
                  <span className="small">{STATUS_LABEL[s.status] || s.status}</span>
                  <span className="bold tabular">{s.n}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h3>Top nurses</h3>
            </div>
            {stats.topNurses.map((n) => (
              <Link key={n.id} to={`/nurses/${n.id}`} className="list-row">
                <Avatar name={n.name} avatar={n.avatar} size="sm" />
                <div className="grow">
                  <div className="list-title small truncate">{n.name}</div>
                  <Stars value={n.rating_avg} count={n.rating_count} />
                </div>
                <span className="small bold nowrap tabular">{currency(n.revenue)}</span>
              </Link>
            ))}
          </section>
        </aside>
      </div>
    </>
  );
}
